import { formatCurrency } from '@/utils/money';
import {
  formatUnitPriceDisplay,
  hasSecondaryPriceDisplay,
} from '@/domain/pricing/utils/pricing-utils';
import {
  PriceEditor,
  PriceEditorState,
} from '@/domain/pricing/services/price-editor';
import { useMemo } from 'react';

interface UsePriceEditUnitDisplayParams {
  editor: PriceEditor | undefined;
  editorState: PriceEditorState | null;
  priceDisplayString: string;
  unitCount: number;
  unitPriceInfo?: string;
  unitSuffix?: string;
}

interface PriceEditUnitDisplay {
  isPerUnit: boolean;
  secondaryPriceDisplay: string | undefined;
  showSecondaryPrice: boolean;
  unitBadgeLabel: string | undefined;
}

/**
 * Unit-aware presentation for a price input: the badge shown when the rep
 * prices per unit, and the opposing price shown alongside it.
 *
 * @param params.priceDisplayString - Pack price fallback used before an editor exists
 * @param params.unitSuffix - Abbreviated unit carried on the row, absent when the item has no unit price
 * @returns The badge label (absent when pricing per pack) and the secondary price
 */
export const usePriceEditUnitDisplay = ({
  editor,
  editorState,
  priceDisplayString,
  unitCount,
  unitPriceInfo,
  unitSuffix,
}: UsePriceEditUnitDisplayParams): PriceEditUnitDisplay => {
  const isPerUnit = editor?.isPricingPerUnit() ?? false;
  const showSecondaryPrice = hasSecondaryPriceDisplay(unitCount);
  const currencyCode = editor?.getCurrencyCode();

  const liveSecondaryPriceDisplay = useMemo(() => {
    if (!showSecondaryPrice) {
      return undefined;
    }

    if (isPerUnit) {
      const packMicros = editor?.getPackPriceInMicros() ?? null;
      return packMicros !== null
        ? formatCurrency(packMicros, currencyCode)
        : priceDisplayString;
    }

    return (
      formatUnitPriceDisplay(
        editorState?.salesRepPriceInMicros ?? null,
        currencyCode,
        unitSuffix,
      ) || unitPriceInfo
    );
  }, [
    currencyCode,
    editor,
    editorState?.salesRepPriceInMicros,
    isPerUnit,
    priceDisplayString,
    showSecondaryPrice,
    unitPriceInfo,
    unitSuffix,
  ]);

  const staticSecondaryPriceDisplay = showSecondaryPrice
    ? isPerUnit
      ? priceDisplayString
      : unitPriceInfo
    : undefined;

  return {
    isPerUnit,
    secondaryPriceDisplay:
      !editor || !editorState
        ? staticSecondaryPriceDisplay
        : liveSecondaryPriceDisplay,
    showSecondaryPrice,
    unitBadgeLabel: isPerUnit ? unitSuffix : undefined,
  };
};
